/**
 * Workspace assets — binary (or opaque) files that belong to a workspace but
 * are NOT compiler source: images a `.md` doc references (`![](./x.png)`).
 *
 * The text {@link Vfs} (`Map<path, string>`) is what the parser/compiler reads;
 * it only ever holds `.arch` / `.md` / `package.archspace`. Assets are a
 * separate, peer channel keyed by the same POSIX workspace paths but carrying
 * raw bytes — so a PNG round-trips losslessly instead of being mangled into a
 * UTF-8 string. The two travel together (loaders, store, share, default
 * bundle) but never mix.
 */

/** Binary workspace files, keyed by absolute POSIX path. */
export type Assets = ReadonlyMap<string, Uint8Array>;

export const EMPTY_ASSETS: Assets = new Map();

/** Extensions routed to the asset (bytes) channel rather than the text VFS. */
const ASSET_RE = /\.(svg|png|jpe?g|gif|webp|ico|avif)$/i;

/** True for a path/basename that should be stored as a binary asset. */
export function isAssetFile(name: string): boolean {
  return ASSET_RE.test(name);
}

const MIME: Record<string, string> = {
  svg: "image/svg+xml",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  ico: "image/x-icon",
  avif: "image/avif",
};

/** MIME type for an asset path's extension (generic binary fallback). */
export function assetMime(path: string): string {
  const ext = path.slice(path.lastIndexOf(".") + 1).toLowerCase();
  return MIME[ext] ?? "application/octet-stream";
}

// --- base64 (used by the default-source bundle + share links) ---------------

/** Bytes → base64 (standard alphabet). Works in browser + node (no Buffer). */
export function bytesToBase64(bytes: Uint8Array): string {
  let bin = "";
  // Chunk to stay clear of the argument-count limit of String.fromCharCode.
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}

/** base64 → bytes (inverse of {@link bytesToBase64}). */
export function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
